import { useMediaQuery } from '@mui/material';
import { useMemo } from 'react';
import type { DashboardMobileNavigationValue, DashboardWidgetSidebarItem } from '../components';

interface UseDashboardMobileViewProps {
  dashboardWidgetSideWidgets: DashboardWidgetSidebarItem[];
  isDashboardSelectionSidebarOpen: boolean;
  isDashboardWidgetSidebarOpen: boolean;
  isDashboardLayoutEditing: boolean;
  handleDashboardSelectionToggle: () => void;
  handleCloseAllSidebarWidgets: () => void;
  handleSetDashboardWidgetsWidget: () => void;
  handleSetVariablesWidget: () => void;
  handleEditDashboardLayout: () => void;
  handleRequestSaveDashboardLayout: () => void;
}

interface UseDashboardMobileViewResult {
  isMobileDashboardView: boolean;
  activeMobileNavigationValue: DashboardMobileNavigationValue;
  handleMobileDashboardAction: () => void;
  handleMobileLayoutAction: () => void;
  handleMobileWidgetsAction: () => void;
  handleMobileVariablesAction: () => void;
  handleMobileDashboardsAction: () => void;
  isMobileRootWidget: (widget: DashboardWidgetSidebarItem) => boolean;
}

export const useDashboardMobileView = ({
  dashboardWidgetSideWidgets,
  isDashboardSelectionSidebarOpen,
  isDashboardWidgetSidebarOpen,
  isDashboardLayoutEditing,
  handleDashboardSelectionToggle,
  handleCloseAllSidebarWidgets,
  handleSetDashboardWidgetsWidget,
  handleSetVariablesWidget,
  handleEditDashboardLayout,
  handleRequestSaveDashboardLayout,
}: UseDashboardMobileViewProps): UseDashboardMobileViewResult => {
  const isMobileDashboardView = useMediaQuery('(max-width:767px)');

  const activeMobileNavigationValue = useMemo<DashboardMobileNavigationValue>(() => {
    if (isDashboardSelectionSidebarOpen) {
      return 'dashboards';
    }

    if (
      dashboardWidgetSideWidgets.some((widget: DashboardWidgetSidebarItem): boolean =>
        ['variables', 'variable-add', 'variable-edit'].includes(widget.type)
      )
    ) {
      return 'variables';
    }

    if (isDashboardWidgetSidebarOpen) {
      return 'widgets';
    }

    return 'dashboard';
  }, [dashboardWidgetSideWidgets, isDashboardSelectionSidebarOpen, isDashboardWidgetSidebarOpen]);

  const handleMobileDashboardAction = (): void => {
    if (isDashboardSelectionSidebarOpen) {
      handleDashboardSelectionToggle();
    }

    if (isDashboardSelectionSidebarOpen || isDashboardWidgetSidebarOpen) {
      handleCloseAllSidebarWidgets();
    }
  };

  const handleMobileLayoutAction = (): void => {
    if (isDashboardLayoutEditing) {
      handleRequestSaveDashboardLayout();
      return;
    }

    handleEditDashboardLayout();
  };

  const handleMobileWidgetsAction = (): void => {
    if (isDashboardSelectionSidebarOpen) {
      handleDashboardSelectionToggle();
    }

    handleSetDashboardWidgetsWidget();
  };

  const handleMobileVariablesAction = (): void => {
    if (isDashboardSelectionSidebarOpen) {
      handleDashboardSelectionToggle();
    }

    handleSetVariablesWidget();
  };

  const handleMobileDashboardsAction = (): void => {
    handleCloseAllSidebarWidgets();
    handleDashboardSelectionToggle();
  };

  const isMobileRootWidget = (widget: DashboardWidgetSidebarItem): boolean => {
    return (
      isMobileDashboardView && (widget.type === 'dashboard-widgets' || widget.type === 'variables')
    );
  };

  return {
    isMobileDashboardView,
    activeMobileNavigationValue,
    handleMobileDashboardAction,
    handleMobileLayoutAction,
    handleMobileWidgetsAction,
    handleMobileVariablesAction,
    handleMobileDashboardsAction,
    isMobileRootWidget,
  };
};
